// src/components/housekeeping/TaskChecklist.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';

function TaskChecklist({ staff, selectedDate, userData }) {
  const [tasks, setTasks] = useState([]);
  const [taskLogs, setTaskLogs] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [attendanceData, setAttendanceData] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';

  useEffect(() => {
    fetchTasks();
    fetchAttendanceData();
  }, [selectedDate]);

  useEffect(() => {
    // Filter available staff based on attendance
    const presentStaffIds = attendanceData
      .filter(a => a.status === 'present' || a.status === 'half_day')
      .map(a => a.staff_id);
    
    const availableStaffList = staff.filter(s => {
      // Check if staff has attendance marked
      const attendance = attendanceData.find(a => a.staff_id === s.id);
      
      // If no attendance marked, consider them as available (but show warning)
      if (!attendance) return true;
      
      // Only present and half-day staff are available for tasks
      return attendance.status === 'present' || attendance.status === 'half_day';
    });
    
    setAvailableStaff(availableStaffList);
    
    // Reset selected staff if they're not available
    if (selectedStaff && !availableStaffList.find(s => s.id === selectedStaff)) {
      setSelectedStaff('');
      setTaskLogs({});
    }
  }, [attendanceData, staff, selectedStaff]);

  useEffect(() => {
    if (selectedStaff) {
      fetchTaskLogs();
    }
  }, [selectedDate, selectedStaff]);

  const fetchTasks = async () => {
    try {
      const { data, error } = await supabase
        .from('housekeeping_tasks')
        .select('*')
        .eq('is_active', true)
        .order('task_name');

      if (!error) {
        setTasks(data || []);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchAttendanceData = async () => {
    try {
      const { data, error } = await supabase
        .from('housekeeping_attendance')
        .select('*')
        .eq('attendance_date', selectedDate);

      if (!error) {
        setAttendanceData(data || []);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const fetchTaskLogs = async () => {
    if (!selectedStaff) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('housekeeping_task_log')
        .select('*')
        .eq('staff_id', selectedStaff)
        .eq('task_date', selectedDate);

      if (!error && data) {
        const logsMap = {};
        data.forEach(log => {
          logsMap[log.task_id] = log;
        });
        setTaskLogs(logsMap);
      }
    } catch (error) {
      console.error('Error fetching task logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = async (taskId, completed) => {
    if (!selectedStaff) {
      alert('Please select a staff member first');
      return;
    }

    // Check if staff is available
    const staffAttendance = attendanceData.find(a => a.staff_id === selectedStaff);
    if (staffAttendance && (staffAttendance.status === 'absent' || staffAttendance.status === 'leave')) {
      alert('Cannot assign tasks to staff members who are absent or on leave');
      return;
    }

    try {
      if (taskLogs[taskId]) {
        // Update existing log
        const { error } = await supabase
          .from('housekeeping_task_log')
          .update({
            completed,
            completed_time: completed ? new Date().toTimeString().split(' ')[0] : null,
            verified_by: userData.id
          })
          .eq('id', taskLogs[taskId].id);

        if (error) throw error;
      } else {
        // Create new log
        const { error } = await supabase
          .from('housekeeping_task_log')
          .insert([{
            staff_id: selectedStaff,
            task_id: taskId,
            task_date: selectedDate,
            completed,
            completed_time: completed ? new Date().toTimeString().split(' ')[0] : null,
            verified_by: userData.id
          }]);

        if (error) throw error;
      }

      fetchTaskLogs();
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task');
    }
  };

  const getStaffStatus = (staffId) => {
    const attendance = attendanceData.find(a => a.staff_id === staffId);
    if (!attendance) return { status: 'not_marked', color: '#6c757d' };
    
    const statusConfig = {
      present: { status: 'Present', color: '#28a745' },
      half_day: { status: 'Half Day', color: '#ffc107' },
      absent: { status: 'Absent', color: '#dc3545' },
      leave: { status: 'On Leave', color: '#17a2b8' }
    };
    
    return statusConfig[attendance.status] || { status: 'Unknown', color: '#6c757d' };
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  const groupedTasks = tasks.reduce((acc, task) => {
    const category = task.task_category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(task);
    return acc;
  }, {});

  const getCompletionStats = () => {
    const totalTasks = tasks.length;
    const completedTasks = Object.values(taskLogs).filter(log => log.completed).length;
    const percentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    return { total: totalTasks, completed: completedTasks, percentage };
  };

  const stats = getCompletionStats();

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h3>Task Checklist for {new Date(selectedDate).toLocaleDateString()}</h3>
        
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginTop: '15px', flexWrap: 'wrap' }}>
          <select
            value={selectedStaff}
            onChange={(e) => setSelectedStaff(e.target.value)}
            className="form-select"
            style={{
              flex: 1,
              maxWidth: '300px',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '8px'
            }}
          >
            <option value="">Select Staff Member</option>
            {staff.map(member => {
              const status = getStaffStatus(member.id);
              const isAvailable = status.status === 'Present' || status.status === 'Half Day' || status.status === 'not_marked';
              
              return (
                <option 
                  key={member.id} 
                  value={member.id}
                  disabled={!isAvailable}
                  style={{
                    color: isAvailable ? 'inherit' : '#999'
                  }}
                >
                  {member.name} {status.status !== 'not_marked' && `(${status.status})`}
                </option>
              );
            })}
          </select>

          {selectedStaff && (
            <>
              <div style={{
                padding: '10px 20px',
                background: '#e7f3ff',
                borderRadius: '8px',
                display: 'inline-block'
              }}>
                <strong>{stats.completed}/{stats.total}</strong> tasks completed
                <span style={{
                  marginLeft: '10px',
                  padding: '2px 8px',
                  background: stats.percentage === 100 ? '#28a745' : '#ffc107',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '12px'
                }}>
                  {stats.percentage}%
                </span>
              </div>

              {(() => {
                const status = getStaffStatus(selectedStaff);
                return status.status !== 'not_marked' && (
                  <div style={{
                    padding: '10px 20px',
                    background: status.color + '20',
                    color: status.color,
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    Staff Status: {status.status}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {selectedStaff && (() => {
          const attendance = attendanceData.find(a => a.staff_id === selectedStaff);
          if (!attendance) {
            return (
              <div style={{
                marginTop: '10px',
                padding: '10px',
                background: '#fff3cd',
                color: '#856404',
                borderRadius: '8px',
                fontSize: '13px'
              }}>
                ⚠️ Warning: Attendance not marked for this staff member. Please mark attendance first.
              </div>
            );
          }
          if (attendance.status === 'absent' || attendance.status === 'leave') {
            return (
              <div style={{
                marginTop: '10px',
                padding: '10px',
                background: '#f8d7da',
                color: '#721c24',
                borderRadius: '8px',
                fontSize: '13px'
              }}>
                ❌ This staff member is {attendance.status === 'absent' ? 'absent' : 'on leave'} today and cannot be assigned tasks.
              </div>
            );
          }
          return null;
        })()}
      </div>

      {selectedStaff ? (
        <div>
          {Object.entries(groupedTasks).map(([category, categoryTasks]) => (
            <div key={category} style={{ marginBottom: '30px' }}>
              <h4 style={{
                textTransform: 'capitalize',
                marginBottom: '15px',
                padding: '8px',
                background: '#f8f9fa',
                borderRadius: '5px'
              }}>
                {category} Tasks
              </h4>
              
              <div style={{ display: 'grid', gap: '10px' }}>
                {categoryTasks.map(task => {
                  const log = taskLogs[task.id];
                  const isCompleted = log?.completed || false;
                  const staffAttendance = attendanceData.find(a => a.staff_id === selectedStaff);
                  const canEdit = isToday && staffAttendance && 
                    (staffAttendance.status === 'present' || staffAttendance.status === 'half_day');
                  
                  return (
                    <div
                      key={task.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        background: isCompleted ? '#e8f5e9' : 'white',
                        border: '1px solid',
                        borderColor: isCompleted ? '#4caf50' : '#ddd',
                        borderRadius: '8px',
                        opacity: canEdit ? 1 : 0.7
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isCompleted}
                        onChange={(e) => toggleTask(task.id, e.target.checked)}
                        disabled={!canEdit || loading}
                        style={{
                          width: '20px',
                          height: '20px',
                          marginRight: '15px',
                          cursor: canEdit ? 'pointer' : 'not-allowed'
                        }}
                      />
                      
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500' }}>
                          {task.task_name}
                        </div>
                        {task.description && (
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                            {task.description}
                          </div>
                        )}
                        {log?.completed_time && (
                          <div style={{ fontSize: '11px', color: '#4caf50', marginTop: '5px' }}>
                            ✔ Completed at {log.completed_time}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: '#f8f9fa',
          borderRadius: '8px',
          color: '#666'
        }}>
          Please select a staff member to view/manage their tasks
        </div>
      )}
    </div>
  );
}

export default TaskChecklist;